/**
 * Landing 3D stage — scroll-driven camera, mobile-first.
 *
 *  • Horizontal: the stage is a REAL horizontal scroll container (`.stage-scroller`).
 *    scrollLeft maps linearly to camera yaw (−180°…+180°), so touch fling / trackpad /
 *    shift-wheel all work natively with inertia. Mouse drag is bridged to scrollLeft.
 *  • Vertical: page scroll is never hijacked. The canvas's position in the viewport
 *    drives camera pitch in −30°…+30°, both directions fully reachable:
 *      page top (canvas at its lowest)  → +30°  camera above, looking down
 *      canvas centred in the viewport    →   0°
 *      canvas leaving through the top    → −30°  camera below, looking up at the underside
 *    The upward half is normalised by the scroll distance actually available above the
 *    centred position (so scrolling back to the page top always reaches +30°); the
 *    downward half by the distance until the canvas exits the viewport.
 *  • Floor is single-sided (FrontSide): seen from below it is culled, so the underside
 *    of the chair shows through instead of a black disc.
 *  • Model: `data-model` (GLB/GLTF) on #stage; a procedural placeholder chair is drawn
 *    if the file is missing so the interaction can be tested without assets. The whole
 *    tableau is derived from ONE reference body — a 170 cm, M-size mannequin built from
 *    capsules (buildMannequin170) — whose joint points (see `J`) position the chair's
 *    surfaces, the keyboard under its hands and the monitor on its gaze ray. Two
 *    supporting props are built procedurally from real spec sheets (no exported mesh
 *    exists yet for either — see openchair-studio/products/*\/refs/SOURCES.md): the G95NC
 *    monitor (curvature and panel dimensions are real; see buildMonitorG95NC) and the
 *    NocFree & split keyboard (footprint is an ESTIMATE — the source only gives key
 *    count/weight/tilt, not overall dimensions; see buildKeyboardNocFreeHalf), one half
 *    per hand on a tented plane at each armrest end (buildArmPlane). All of it is
 *    rough volumes, not detailed depictions — a diagram, not a product shot.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const stage = document.getElementById("stage");
const canvas = document.getElementById("stage-canvas");
if (stage && canvas) init(stage, canvas);

function init(stage, canvas) {
  const scroller = stage.querySelector(".stage-scroller");
  const track = stage.querySelector(".stage-track");
  const YAW_RANGE = Math.PI * 2;      // full turn across the scroll extent
  const PITCH_MAX = THREE.MathUtils.degToRad(30);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x111111);
  const camera = new THREE.PerspectiveCamera(35, 1, 0.05, 100);

  scene.add(new THREE.HemisphereLight(0xffffff, 0x333333, 1.2));
  const key = new THREE.DirectionalLight(0xffffff, 1.6);
  key.position.set(2, 4, 3);
  scene.add(key);
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(2.2, 48),
    new THREE.MeshStandardMaterial({ color: 0x1c1c1c, roughness: 1, side: THREE.FrontSide }),
  );
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  // Orbit target + distance are set once the model's bounding box is known.
  const target = new THREE.Vector3(0, 0.5, 0);
  let distance = 3;
  const state = { yaw: 0, pitch: 0 };          // targets (from scroll)
  const smooth = { yaw: 0, pitch: 0 };         // eased values (rendered)
  let dirty = true;

  function placeCamera() {
    const { yaw, pitch } = smooth;
    camera.position.set(
      target.x + distance * Math.cos(pitch) * Math.sin(yaw),
      target.y + distance * Math.sin(pitch),
      target.z + distance * Math.cos(pitch) * Math.cos(yaw),
    );
    camera.lookAt(target);
  }

  function resize() {
    const w = scroller.clientWidth, h = scroller.clientHeight;
    if (canvas.width !== Math.floor(w * renderer.getPixelRatio()) || canvas.height !== Math.floor(h * renderer.getPixelRatio())) {
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    dirty = true;
  }

  // ---- horizontal: scrollLeft → yaw ----
  function readYaw() {
    const max = scroller.scrollWidth - scroller.clientWidth;
    const t = max > 0 ? scroller.scrollLeft / max : 0.5;   // 0..1
    state.yaw = (t - 0.5) * YAW_RANGE;
    dirty = true;
  }
  function centerScroll() {
    scroller.scrollLeft = (scroller.scrollWidth - scroller.clientWidth) / 2;
    readYaw();
  }
  scroller.addEventListener("scroll", readYaw, { passive: true });

  // Mouse drag → scrollLeft (touch already pans natively; vertical touch chains to the page).
  let drag = null;
  scroller.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "mouse") return;
    drag = { x: e.clientX, left: scroller.scrollLeft };
    scroller.setPointerCapture(e.pointerId);
  });
  scroller.addEventListener("pointermove", (e) => {
    if (!drag) return;
    scroller.scrollLeft = drag.left - (e.clientX - drag.x);
  });
  const endDrag = () => { drag = null; };
  scroller.addEventListener("pointerup", endDrag);
  scroller.addEventListener("pointercancel", endDrag);
  // Plain wheel over the stage = page scroll (untouched). Horizontal wheel/trackpad = yaw (native).

  // ---- vertical: canvas position in viewport → pitch ----
  function readPitch() {
    const r = scroller.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const canvasMid = r.top + r.height / 2;      // viewport coords
    const viewMid = vh / 2;
    const offset = viewMid - canvasMid;          // >0 once the canvas is above centre
    let t;
    if (offset >= 0) {
      // Downward half: 0 at centre → +1 when the canvas has fully left through the top.
      t = Math.min(offset / (vh / 2 + r.height / 2), 1);
    } else {
      // Upward half: 0 at centre → −1 at the page top (max scroll-up available).
      // scrollToCenter = how far we'd still need to scroll down to centre the canvas;
      // at the page top the available upward travel is scrollY + 0 ⇒ use the distance
      // between "page top" and "centred" as the normaliser so +30° is always reachable.
      const toCenter = -offset;                  // px of further scrolling needed to centre
      const centerScrollY = scrollY + toCenter;  // absolute scrollY where the canvas is centred
      t = centerScrollY > 0 ? -Math.min(toCenter / centerScrollY, 1) : 0;
    }
    // canvas up (t>0) → camera goes DOWN (negative elevation) and looks up at the underside;
    // canvas down / page scrolled up (t<0) → camera rises and looks down.
    state.pitch = -t * PITCH_MAX;
    dirty = true;
  }
  window.addEventListener("scroll", readPitch, { passive: true });
  window.addEventListener("resize", () => { resize(); readPitch(); readYaw(); });

  // ---- rig animation: two modes, held, with a move between ----
  // Shape animation (2026-09-19). Mode 0 = resting recline (backrest 35° from vertical);
  // mode 1 = lying (backrest 15° off the ground). Between them the whole rig rotates
  // rigidly about the hip joint, and the legrest + shins + feet additionally drop about
  // the knee line so the toes end below the knee when lying (the legrest keeps its
  // ground angle while everything else reclines past it). Each mode is HELD for
  // RIG_HOLD_MS, each move takes RIG_MOVE_MS with a half-cosine ease. Runs only while
  // the stage is on screen, never under prefers-reduced-motion. (Angles are defined
  // with the pose constants below, since they derive from RECLINE_TILT_DEG etc.)
  const RIG_HOLD_MS = 5000, RIG_MOVE_MS = 5000;
  const RIG_CYCLE_MS = 2 * (RIG_HOLD_MS + RIG_MOVE_MS);
  const ease = (u) => (1 - Math.cos(Math.PI * u)) / 2;
  function rigPhase(now) {            // 0 = mode 0, 1 = mode 1
    const t = now % RIG_CYCLE_MS;
    if (t < RIG_HOLD_MS) return 0;
    if (t < RIG_HOLD_MS + RIG_MOVE_MS) return ease((t - RIG_HOLD_MS) / RIG_MOVE_MS);
    if (t < 2 * RIG_HOLD_MS + RIG_MOVE_MS) return 1;
    return 1 - ease((t - 2 * RIG_HOLD_MS - RIG_MOVE_MS) / RIG_MOVE_MS);
  }
  let rig = null, seatRig = null, legRig = null;     // pivot groups, set by buildTableau
  let lastPhase = -1;
  let stageVisible = false;
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  if ("IntersectionObserver" in window) {
    new IntersectionObserver((entries) => { stageVisible = entries.some((e) => e.isIntersecting); }, { threshold: 0.05 }).observe(scroller);
  } else {
    stageVisible = true;
  }

  // ---- render loop (on demand + easing) ----
  function frame(now) {
    const k = 0.15;
    smooth.yaw += (state.yaw - smooth.yaw) * k;
    smooth.pitch += (state.pitch - smooth.pitch) * k;
    let settled = Math.abs(state.yaw - smooth.yaw) < 1e-4 && Math.abs(state.pitch - smooth.pitch) < 1e-4;
    if (rig && stageVisible && !reduceMotion) {
      const phase = rigPhase(now || 0);
      if (phase !== lastPhase) {               // holds don't re-render
        rig.rotation.x = RIG_SWING_RAD * phase;
        seatRig.rotation.x = SEAT_OPEN_RAD * phase;
        legRig.rotation.x = LEG_DROP_RAD * phase;
        lastPhase = phase;
        settled = false;
      }
    }
    if (dirty || !settled) {
      placeCamera();
      renderer.render(scene, camera);
      dirty = !settled;
    }
    requestAnimationFrame(frame);
  }

  // ---- model ----
  function fit(object) {
    // Frame the union of the rig's two modes so the move never leaves the shot.
    const { pivot, seat, legs } = object.userData;
    const box = new THREE.Box3();
    for (const p of pivot ? [0, 1] : [0]) {
      if (pivot) { pivot.rotation.x = RIG_SWING_RAD * p; seat.rotation.x = SEAT_OPEN_RAD * p; legs.rotation.x = LEG_DROP_RAD * p; }
      box.union(new THREE.Box3().setFromObject(object));
    }
    if (pivot) { pivot.rotation.x = 0; seat.rotation.x = 0; legs.rotation.x = 0; }
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    object.position.sub(center);               // centre at origin…
    object.position.y += size.y / 2;           // …then rest on the floor
    target.set(0, size.y / 2, 0);
    const radius = Math.max(size.x, size.y, size.z) / 2;
    distance = radius / Math.sin(THREE.MathUtils.degToRad(camera.fov / 2)) * 1.25;
    scene.add(object);
    dirty = true;
  }

  // ---- shared frame: one 170 cm body defines the chair, the props and the gaze ----
  // Chair-local metres, +y up, +z toward the sitter's feet. Everything below is positioned
  // from the same joint points `J`, so the chair surfaces, the mannequin, the keyboard
  // under its hands and the monitor on its gaze ray can't drift apart when a number moves.
  const RECLINE_TILT_DEG = -35;   // backrest from vertical (negative = leaning back)
  const SEAT_TILT_DEG = -8;       // seat pan, front edge up
  const LEGREST_TILT_DEG = 25;    // legrest, front edge down
  // Screen placement rule (2026-09-19, decided with the user): head fully on the backrest,
  // chin at 0°, panel PARALLEL to the backrest at 1 m along the face normal, with the eye
  // line one third down from the panel's top edge. The earlier version tilted the panel
  // 15° toward a depressed gaze ray (the lab note's 15–25° applied to the screen centre);
  // from the side that read as "head bowed", which is exactly what the head support is
  // there to prevent. The 15–25° figure is a standard-monitor centreline rule; this 57"
  // panel spans ~24° vertically at 1 m, so the 1/3 rule keeps it inside a comfortable
  // range (+8° … −16°) instead. Centre ends up ~4° below the face normal -- little of
  // Pansell's eyelid benefit, and we say so rather than pretend otherwise.
  const GAZE_DROP_DEG = 0;
  const VIEW_DISTANCE = 1.0;
  const EYE_LINE_FROM_TOP = 1 / 3;
  const MONITOR_PANEL_H = 0.4295;    // G95NC height without stand (see buildMonitorG95NC)
  const rotX = (x, y, z, deg) =>
    new THREE.Vector3(x, y, z).applyAxisAngle(new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(deg));
  const UP_BACK = rotX(0, 1, 0, RECLINE_TILT_DEG);     // along the backrest, upward
  const OUT_BACK = rotX(0, 0, 1, RECLINE_TILT_DEG);    // backrest surface normal = face normal at rest
  const ALONG_SEAT = rotX(0, 0, 1, SEAT_TILT_DEG);
  const UP_SEAT = rotX(0, 1, 0, SEAT_TILT_DEG);
  const ALONG_LEG = rotX(0, 0, 1, LEGREST_TILT_DEG);
  const UP_LEG = rotX(0, 1, 0, LEGREST_TILT_DEG);
  const GAZE = rotX(0, 0, 1, RECLINE_TILT_DEG + GAZE_DROP_DEG);
  const FOOT_DIR = rotX(0, 0, 1, LEGREST_TILT_DEG - 60); // relaxed ankle: ~30° plantar-flexed from the shin's perpendicular
  // Rig swing (see the animation block above): backrest 35° → 75° from vertical = −40° about x.
  const RIG_TARGET_FROM_GROUND_DEG = 15;
  const RIG_SWING_RAD = THREE.MathUtils.degToRad(-((90 - RIG_TARGET_FROM_GROUND_DEG) + RECLINE_TILT_DEG));
  // Lying mode: legrest + shins + feet rotate about the knee line by +40° (positive x
  // rotation = feet down). Equal and opposite to the rig swing, so the legrest holds its
  // 25°-below-horizontal ground angle while the torso reclines past it; the toes end
  // ~6 cm below the knee (user: toes must come below the knee when lying).
  const LEG_DROP_RAD = -RIG_SWING_RAD;
  // Lying mode, second relative motion (2026-09-19): the seat pan opens 20° more at its
  // hinge with the backrest (the seat slab's rear edge), taking the pelvis, thighs and
  // the whole leg assembly with it, so the hip angle opens and the legs extend. The
  // backrest and upper body don't move; the legrest keeps its drop RELATIVE TO THE SEAT.
  // Positive x rotation = seat front down.
  const SEAT_OPEN_RAD = THREE.MathUtils.degToRad(20);
  // (SEAT_HINGE, the hinge point, is defined after `J` below -- it derives from the hip.)

  // 170 cm, M-size segment lengths (m). Standing height sums to ~1.70:
  // foot 0.07 + shin 0.41 + thigh 0.44 + hip→shoulder 0.50 + neck 0.07 + head 0.21.
  const B = {
    torso: 0.50, neck: 0.07, headR: 0.105, thigh: 0.44, shin: 0.41,
    upperArm: 0.30, forearm: 0.26, hand: 0.17,
    shoulderHalf: 0.21, elbowHalf: 0.25, hipHalf: 0.09,   // elbows a little out from the body: forearms on the armrest centrelines
  };
  const J = (() => {
    const hip = new THREE.Vector3(0, 0.56, -0.20);                    // hip joint; the chair is built around it
    const shoulder = hip.clone().addScaledVector(UP_BACK, B.torso);
    const neckTop = shoulder.clone().addScaledVector(UP_BACK, B.neck);
    const head = neckTop.clone().addScaledVector(UP_BACK, B.headR);   // head centre, resting on the backrest
    const eye = head.clone().addScaledVector(OUT_BACK, B.headR * 0.9);
    const knee = hip.clone().addScaledVector(ALONG_SEAT, B.thigh);
    const ankle = knee.clone().addScaledVector(ALONG_LEG, B.shin);
    const elbow = shoulder.clone().addScaledVector(UP_BACK, -B.upperArm);   // upper arm hangs along the torso
    const wrist = elbow.clone().add(new THREE.Vector3(0, 0.055, B.forearm)); // forearm on a gravity-horizontal shelf, wrist lifted onto the keyboard (see the plane constants)
    const fingertip = wrist.clone().add(new THREE.Vector3(0, 0.015, B.hand));
    return { hip, shoulder, neckTop, head, eye, knee, ankle, elbow, wrist, fingertip };
  })();
  const ARM_TOP_Y = J.elbow.y - 0.045;   // armrest top: just under the forearm (r 0.04)
  // Seat/backrest hinge for the lying-mode seat opening: the seat slab's rear edge, bottom.
  const SEAT_HINGE = J.hip.clone().addScaledVector(ALONG_SEAT, -0.12).addScaledVector(UP_SEAT, -0.13);
  // Input plane at the end of each armrest. The keyboard half sits on it, not on the
  // armrest itself, and the plane is tented so the OUTER edge drops -- the wrist stays in
  // a neutral half-pronated angle instead of rolling flat onto the keys (2026-09-18,
  // user: a flat-on-armrest keyboard forces wrist pronation, the carpal-tunnel failure
  // mode). The armrest runs the full length underneath so typing/mousing/drawing loads
  // go into it rather than a cantilever (user: a cantilever would vibrate). The plane
  // rests on the armrest's OUTER top edge and tilts up from there, so nothing sinks into
  // the armrest; the wedge that would fill the gap on the inner side is omitted.
  // Each plane is wide enough to carry the rest of the user's actual desk (photo,
  // 2026-09-18): keyboard half over the forearm, trackpad outboard on the left, mouse +
  // numpad outboard on the right -- left/right as the SITTER sees them (they face +z,
  // so their right is −x).
  const ARM_X = B.elbowHalf;              // armrest centreline = forearm line
  const ARM_W = 0.16;
  const PLANE_THICK = 0.03;
  const PLANE_TENT_DEG = 15;
  const PLANE_LEN = 0.20;                 // along z
  const PLANE_IN = 0.18, PLANE_OUT = 0.24;   // extent from the pivot line, inboard / outboard (0.42 wide)
  const PLANE_PIVOT_X = ARM_X + ARM_W / 2;   // pivot = plane underside on the armrest's outer top edge
  const PLANE_Z0 = J.wrist.z + 0.01;      // plane starts 1 cm past the wrist
  const ARM_LEN = PLANE_Z0 + PLANE_LEN - (J.elbow.z - 0.08);   // from behind the elbow to the plane's far end
  // Yaw (2026-09-19, user): square to the armrest, the plane's outer end is farther from
  // the elbow than its inner end, so reaching the mouse/numpad/trackpad means extending
  // the arm. Swing the outer end back toward the body until BOTH ends are equidistant
  // from the elbow, i.e. the elbow lies on the plane's perpendicular bisector:
  //   (E − O)·axis = m,  axis = (cos φ, −sin φ) in the xz plane (s = +1 side),
  // where O is the pivot line's midpoint, E the elbow, m the plane centre's offset along
  // the axis. a·cos φ + b·sin φ = c  ⇒  φ = atan2(b, a) − acos(c / √(a²+b²)).
  const PLANE_YAW_RAD = (() => {
    const a = ARM_X - PLANE_PIVOT_X;                                     // E−O, x
    const b = -(J.elbow.z - (PLANE_Z0 + PLANE_LEN / 2));                 // −(E−O), z
    const c = ((PLANE_OUT - PLANE_IN) / 2) * Math.cos(THREE.MathUtils.degToRad(PLANE_TENT_DEG));
    return Math.atan2(b, a) - Math.acos(c / Math.hypot(a, b));
  })();

  // Placeholder chair, sized to the body above: seat under the thighs, backrest behind the
  // torso, legrest under the shins, armrests under the forearms. Floor post intentionally
  // omitted (2026-09-18): the point of this scene is the relationship between body, seat,
  // back and screen, not how the chair is held up.
  // `legs` (optional): a group in chair-local coordinates that the tableau rotates about
  // the knee line; the legrest goes there so it can drop in lying mode.
  function placeholderChair({ seat, legs } = {}) {
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0x6f6a63, roughness: 0.8 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.5, metalness: 0.4 });
    const W = 0.66;   // shell width: M-size shoulders (0.42) plus margin
    const slab = (width, len, center, deg, into = g) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(width, 0.08, len), mat);
      m.position.copy(center);
      m.rotation.x = THREE.MathUtils.degToRad(deg);
      into.add(m);
    };
    // Seat: surface 0.09 below the hip joint, from 0.12 behind the hip to 0.02 short of the knee.
    slab(W, 0.54, J.hip.clone().addScaledVector(ALONG_SEAT, 0.15).addScaledVector(UP_SEAT, -(0.09 + 0.04)), SEAT_TILT_DEG, seat ?? g);
    // Backrest: surface 0.12 behind the torso axis, from 0.10 below the hip to 0.12 above the head.
    // A slab is long along z, so +90° stands it up; the tilt is added on top.
    slab(W, 1.0, J.hip.clone().addScaledVector(UP_BACK, 0.40).addScaledVector(OUT_BACK, -(0.12 + 0.04)), 90 + RECLINE_TILT_DEG);
    // Legrest: surface 0.08 below the shin axis (room for the heel), running past the ankle.
    slab(0.50, 0.55, J.knee.clone().addScaledVector(ALONG_LEG, 0.295).addScaledVector(UP_LEG, -(0.08 + 0.04)), LEGREST_TILT_DEG, legs ?? g);
    for (const s of [-1, 1]) {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(ARM_W, 0.06, ARM_LEN), dark);
      arm.position.set(s * ARM_X, ARM_TOP_Y - 0.03, PLANE_Z0 + PLANE_LEN - ARM_LEN / 2);   // runs under the whole input plane
      g.add(arm);
    }
    return g;
  }

  /** Reference sitter: 170 cm, M-size, capsules only. Poses itself from `J`.
   *  `legs` (optional): knee-line group for the shins and feet (see placeholderChair). */
  function buildMannequin170({ seat, legs } = {}) {
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0xe8dfd0, roughness: 0.6 });
    const Y = new THREE.Vector3(0, 1, 0);
    // Capsule from a to b; `widen` stretches it sideways (local x = world x for
    // segments in the sagittal plane, which all of these are).
    const limb = (a, b, r, widen = 1, into = g) => {
      const len = a.distanceTo(b);
      const m = new THREE.Mesh(new THREE.CapsuleGeometry(r, Math.max(len - 2 * r, 0.001), 4, 12), mat);
      m.position.copy(a).lerp(b, 0.5);
      m.quaternion.setFromUnitVectors(Y, b.clone().sub(a).normalize());
      m.scale.x = widen;
      into.add(m);
      return m;
    };
    const side = (v, dx) => v.clone().add(new THREE.Vector3(dx, 0, 0));

    limb(J.hip, J.shoulder, 0.11, 1.6);                     // torso: 0.35 wide, 0.22 thick
    limb(side(J.hip, -0.08), side(J.hip, 0.08), 0.10, 1, seat ?? g);   // pelvis rides with the seat pan
    limb(J.shoulder, J.neckTop, 0.05);                      // neck
    const head = new THREE.Mesh(new THREE.SphereGeometry(B.headR, 24, 16), mat);
    head.position.copy(J.head);
    head.scale.set(0.9, 1, 1);
    head.rotation.x = THREE.MathUtils.degToRad(RECLINE_TILT_DEG);
    g.add(head);
    for (const s of [-1, 1]) {
      limb(side(J.shoulder, s * B.shoulderHalf), side(J.elbow, s * B.elbowHalf), 0.045);
      limb(side(J.elbow, s * B.elbowHalf), side(J.wrist, s * B.elbowHalf), 0.04);
      // Flat hand, rolled about its own axis to lie on the tented plane (outer edge down).
      limb(side(J.wrist, s * B.elbowHalf), side(J.fingertip, s * B.elbowHalf), 0.018, 2.4)
        .rotateY(-s * THREE.MathUtils.degToRad(PLANE_TENT_DEG));
      limb(side(J.hip, s * B.hipHalf), side(J.knee, s * B.hipHalf), 0.07, 1, seat ?? g);   // thigh rides with the seat pan
      limb(side(J.knee, s * B.hipHalf), side(J.ankle, s * B.hipHalf), 0.055, 1, legs ?? g);
      const heel = side(J.ankle, s * B.hipHalf).addScaledVector(FOOT_DIR, -0.05);
      limb(heel, heel.clone().addScaledVector(FOOT_DIR, 0.24), 0.035, 1.3, legs ?? g);
    }
    return g;
  }

  /**
   * Samsung Odyssey Neo G9 57" G95NC — dimensions from Samsung's own spec sheet /
   * manual (openchair-studio/products/monitor-g95nc/refs/SOURCES.md §1). Screen
   * curvature is real (1000R over a 1327.5 mm chord ⇒ ~83.3° of arc, per that
   * file's own §3 modeling notes). Panel only, no stand; a thin dark shell behind
   * it stands in for the housing — a silhouette, not a render.
   */
  // Small generated "desktop" texture so the screen reads as a lit monitor rather than
  // a dark slab that disappears into the scene's black background. Not a real wallpaper
  // (no canvas access outside a browser, and this is a diagram, not a product shot) --
  // a gradient plus a few soft window rectangles and a taskbar strip is enough to signal
  // "screen" at the scale this renders at.
  function makeScreenTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 330; // ≈ panelH / chord aspect ratio below
    const ctx = canvas.getContext("2d");
    // The screen is rendered on the cylinder's INSIDE (BackSide), which mirrors the
    // u axis relative to the outside view the geometry's UVs assume -- so draw the
    // whole image mirrored once here and it comes out the right way round for the sitter.
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    grad.addColorStop(0, "#13233a");
    grad.addColorStop(0.55, "#2c5f6e");
    grad.addColorStop(1, "#13233a");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(255,255,255,0.10)";
    ctx.fillRect(canvas.width * 0.08, canvas.height * 0.16, canvas.width * 0.26, canvas.height * 0.58);
    ctx.fillRect(canvas.width * 0.40, canvas.height * 0.26, canvas.width * 0.20, canvas.height * 0.46);
    ctx.fillRect(canvas.width * 0.66, canvas.height * 0.18, canvas.width * 0.26, canvas.height * 0.52);
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, canvas.height * 0.93, canvas.width, canvas.height * 0.07);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  function buildMonitorG95NC() {
    const g = new THREE.Group();
    const R = VIEW_DISTANCE;                     // 1000 mm curvature radius = the viewing distance, by design
    const chord = 1.3275;                        // width without stand
    const halfAngle = Math.asin(chord / 2 / R);  // ≈ 41.6°
    const arc = halfAngle * 2;                   // ≈ 83.3° of the cylinder
    const panelH = MONITOR_PANEL_H;              // height without stand
    const segment = (r, h) => new THREE.CylinderGeometry(r, r, h, 48, 1, true, Math.PI - halfAngle, arc);

    // Screen: the concave inside of an open cylinder segment. thetaStart = π−halfAngle
    // puts the arc's apex at local z = −R, so the concave face looks back at the group's
    // local origin -- which is placed at the sitter's eyes. CylinderGeometry's faces
    // point OUTWARD (convex side), so the image must be drawn on the BackSide; with the
    // default FrontSide the picture showed up on the rear of the monitor (2026-09-18).
    // MeshBasicMaterial: a screen is self-lit, so it reads the same regardless of lighting.
    const screen = new THREE.Mesh(
      segment(R, panelH),
      new THREE.MeshBasicMaterial({ map: makeScreenTexture(), side: THREE.BackSide }),
    );
    // Housing: same arc, 15 mm further out, FrontSide -- opaque from behind, invisible
    // from the front, so the monitor is a dark shell with a lit face rather than a box.
    const shell = new THREE.Mesh(
      segment(R + 0.015, panelH + 0.02),
      new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.6, side: THREE.FrontSide }),
    );
    g.add(screen, shell);
    return g;
  }

  /**
   * NocFree & split keyboard. Key count (84), weight (1.03 kg) and typing angle
   * (~3°, 26 mm wedge at the tall edge) are real (openchair-studio/products/
   * keyboard-nocfree/refs/SOURCES.md); the studio repo has an actual Keyboard.fbx
   * but no exported web format yet, and the "&" model's overall footprint isn't
   * published, so the per-half size below is an ESTIMATE, not a spec.
   */
  // One half, origin at the centre of its underside, lying flat: it's a split keyboard,
  // so each half lives on its own armrest plane and the plane supplies the tenting.
  function buildKeyboardNocFreeHalf() {
    const mat = new THREE.MeshStandardMaterial({ color: 0xc9c9c9, roughness: 0.35, metalness: 0.6 });
    const halfW = 0.17, halfD = 0.13, tallEdge = 0.026, shortEdge = 0.012;
    // Wedge: taller at the back edge (away from the sitter), tapering toward the front —
    // approximated with a box tilted by the ~3° typing angle rather than a true taper.
    const half = new THREE.Mesh(new THREE.BoxGeometry(halfW, (tallEdge + shortEdge) / 2, halfD), mat);
    half.position.set(0, (tallEdge + shortEdge) / 4, 0);
    half.rotation.x = THREE.MathUtils.degToRad(-3);
    return half;
  }

  // Armrest-end input plane (see PLANE_* above) with the devices on top. Group origin =
  // the pivot line (plane underside on the armrest's outer top edge); rotated about z so
  // the OUTER edge drops. Children are placed in the plane's own tilted frame, +x = outboard
  // for s = +1 and mirrored via `s` for the other side.
  function buildArmPlane(s) {
    const g = new THREE.Group();
    const tent = THREE.MathUtils.degToRad(PLANE_TENT_DEG);
    const plane = new THREE.Mesh(
      new THREE.BoxGeometry(PLANE_IN + PLANE_OUT, PLANE_THICK, PLANE_LEN),
      new THREE.MeshStandardMaterial({ color: 0x8a8580, roughness: 0.7 }),
    );
    plane.position.set(s * (PLANE_OUT - PLANE_IN) / 2, PLANE_THICK / 2, 0);
    g.add(plane);

    // Keyboard half: its top centre must land on the forearm line (world x = ARM_X) so the
    // hand sits on it. In the tilted frame a point (x, h) maps to world dx = x·cos + h·sin
    // (outer-down tilt), so solve for x given the wanted world offset from the pivot.
    const half = buildKeyboardNocFreeHalf();
    const keyTop = PLANE_THICK + 0.019;
    half.position.x = s * ((ARM_X - PLANE_PIVOT_X) - keyTop * Math.sin(tent)) / Math.cos(tent);
    half.position.y += PLANE_THICK;
    g.add(half);

    // Product dummies from the user's own desk photo, outboard of the keyboard half:
    // sizes are the real products' spec footprints, shapes are placeholders.
    const grey = new THREE.MeshStandardMaterial({ color: 0xd8d8d8, roughness: 0.4, metalness: 0.3 });
    // Sides are the SITTER's: they face +z, so their right hand is at −x (s = −1).
    // (2026-09-18: first cut had these mirrored -- mouse on the sitter's left.)
    if (s > 0) {
      // Sitter's left: Magic Trackpad-class slab, 160 × 115 mm, ~8 mm (its wedge averaged).
      const pad = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.008, 0.115), grey);
      pad.position.set(s * 0.11, PLANE_THICK + 0.004, 0);
      g.add(pad);
    } else {
      // Sitter's right: mouse (MX Master-class dome, 85 × 50 × 125 mm) then a numpad
      // (90 × 130 mm, same wedge profile as the keyboard).
      const mouse = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 14), new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5 }));
      mouse.scale.set(0.0425, 0.035, 0.0625);
      mouse.position.set(s * 0.0625, PLANE_THICK + 0.015, 0);   // dome sunk 2 cm into the 3 cm plane: 5 cm proud, flat-bottomed
      g.add(mouse);
      const numpad = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.019, 0.13), grey);
      numpad.position.set(s * 0.17, PLANE_THICK + 0.0095, 0);
      numpad.rotation.x = THREE.MathUtils.degToRad(-3);
      g.add(numpad);
    }

    // Yawing about the pivot midpoint swings one end of the pivot line inboard over the
    // armrest, where the tented underside would dip below the armrest top by up to
    // (PLANE_LEN/2)·sin(yaw)·tan(tent); lift the whole plane by that much.
    const lift = (PLANE_LEN / 2) * Math.sin(PLANE_YAW_RAD) * Math.tan(tent);
    g.position.set(s * PLANE_PIVOT_X, ARM_TOP_Y + lift, PLANE_Z0 + PLANE_LEN / 2);
    // Euler XYZ applies Z first: tent about the plane's own long axis (rotation.z > 0
    // raises the +x end, so the outer edge drops with −s·tent), then the yaw about
    // vertical (+y rotation takes +x toward −z, i.e. the outer end back toward the elbow
    // on the s = +1 side; mirrored for s = −1).
    g.rotation.set(0, s * PLANE_YAW_RAD, -s * tent);
    return g;
  }

  const url = stage.dataset.model;
  const loader = new GLTFLoader();
  const start = () => { resize(); centerScroll(); readPitch(); requestAnimationFrame(frame); };

  // Compose chair + sitter + monitor + keyboard as one group so the camera frames them together.
  // `chairBuilder({ seat, legs })` returns the chair group; it gets the seat-hinge and
  // knee-line groups so it can put its seat pan / legrest there (the GLB path can't, so
  // a loaded chair won't articulate).
  function buildTableau(chairBuilder) {
    const root = new THREE.Group();
    // Everything hangs off a pivot at the hip joint so the whole rig can rotate about
    // the pelvis (rig animation). `g` is the body frame, shifted so the hip sits at the
    // pivot's origin; parts keep using the chair-local coordinates in `J`.
    const pivot = new THREE.Group();
    pivot.position.copy(J.hip);
    const g = new THREE.Group();
    g.position.copy(J.hip).negate();
    pivot.add(g);
    root.add(pivot);
    // Second pivot at the seat/backrest hinge, same pattern: seat pan + pelvis + thighs
    // live in `seat`, and the knee pivot hangs off it so the legs follow the seat.
    const seatPivot = new THREE.Group();
    seatPivot.position.copy(SEAT_HINGE);
    const seat = new THREE.Group();
    seat.position.copy(SEAT_HINGE).negate();
    seatPivot.add(seat);
    g.add(seatPivot);
    // Third pivot on the knee line: legrest + shins + feet live in `legs`.
    const legPivot = new THREE.Group();
    legPivot.position.copy(J.knee);
    const legs = new THREE.Group();
    legs.position.copy(J.knee).negate();
    legPivot.add(legs);
    seat.add(legPivot);
    root.userData.pivot = pivot;
    root.userData.seat = seatPivot;
    root.userData.legs = legPivot;
    rig = pivot;
    seatRig = seatPivot;
    legRig = legPivot;

    g.add(chairBuilder({ seat, legs }));
    g.add(buildMannequin170({ seat, legs }));

    const monitor = buildMonitorG95NC();
    // The monitor group's local origin is the curve's centre of curvature, and its
    // concave face always looks back at that origin -- so put the origin AT the eyes
    // and rotate until the panel lands on the gaze ray. Local +z (the direction the
    // panel faces, i.e. toward the viewer) must map to −GAZE and local +y to the
    // gaze's "up": Ry(π) first (turn the panel to face −z), then Rx(tilt+drop) to
    // follow the face. Euler order XYZ applies Y before X, so rotation.set(x, π, 0).
    // Origin at the eyes, then slid down the panel's own up axis so the eye line sits
    // EYE_LINE_FROM_TOP of the way down the panel (centre is panelH·(1/2 − 1/3) below).
    monitor.position.copy(J.eye).addScaledVector(UP_BACK, -MONITOR_PANEL_H * (0.5 - EYE_LINE_FROM_TOP));
    monitor.rotation.set(THREE.MathUtils.degToRad(RECLINE_TILT_DEG + GAZE_DROP_DEG), Math.PI, 0);
    g.add(monitor);

    // Tented keyboard planes at the armrest ends, one half per hand.
    g.add(buildArmPlane(-1), buildArmPlane(1));

    return root;
  }

  if (url) {
    loader.load(url, (gltf) => { fit(buildTableau(() => gltf.scene)); start(); }, undefined, () => { fit(buildTableau(placeholderChair)); start(); });
  } else {
    fit(buildTableau(placeholderChair));
    start();
  }
  // Initial paint before the model arrives, so the stage isn't blank.
  resize(); placeCamera(); renderer.render(scene, camera);
}
